import { Link, useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Plus, Minus, RotateCcw, SlidersHorizontal, FileText, Gift } from 'lucide-react'
import { PageHeader, Panel, Select, EmptyState } from '../../ds/index'
import { useApi } from '../../lib/useApi'
import { money } from '../../lib/format'
import { Icon } from '../../components/Icon'
import { LoadingBlock, ErrorBlock } from '../../components/Loading'
import { monthName } from './ClientLeads'
import { BillingTabs } from './ClientBilling'

type Entry = {
  id: string; kind: 'deposit' | 'lead' | 'refund' | 'adjustment' | 'gift'
  credit_cents: number; cash_cents: number; method: string | null; reference: string | null; note: string | null
  created_at: string; lead_buyer_id: string | null; summary: string | null; tier_name: string | null
}
type InvoiceLine = { id: string; billed_at: string; overage_cents: number; invoice_cents: number; tier_name: string | null; summary: string | null }
type Statement = {
  month: string; timezone: string; opening_credit_cents: number; closing_credit_cents: number; deposited_cents: number; can_pay_online: boolean
  entries: Entry[]
  invoice: { lines: InvoiceLine[]; total_cents: number; status: 'invoiced' | 'paid' | null; invoiced_cents: number | null; invoiced_at: string | null; paid_at: string | null }
}

const ICON = { deposit: Plus, lead: Minus, refund: RotateCcw, adjustment: SlidersHorizontal, gift: Gift }
const TITLE: Record<Entry['kind'], string> = { deposit: 'Money added', lead: 'Lead', refund: 'Refund', adjustment: 'Correction', gift: 'Sign-up gift' }

/** The twelve months up to this one, newest first, as YYYY-MM. */
function lastTwelve() {
  const d = new Date()
  return Array.from({ length: 12 }, (_, i) => {
    const x = new Date(d.getFullYear(), d.getMonth() - i, 15)
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`
  })
}
const shift = (m: string, by: number) => {
  const [y, mo] = m.split('-').map(Number)
  const x = new Date(y, mo - 1 + by, 15)
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`
}
const signed = (cents: number) => `${cents > 0 ? '+' : cents < 0 ? '-' : ''}${money(Math.abs(cents))}`

export default function ClientStatement() {
  const [params, setParams] = useSearchParams()
  const month = params.get('month') || ''
  const { data, error } = useApi<Statement>(`/api/client/statement${month ? `?month=${encodeURIComponent(month)}` : ''}`)
  const months = lastTwelve()
  if (data && !months.includes(data.month)) months.unshift(data.month)
  const go = (m: string) => setParams({ month: m }, { replace: true })

  const sum = (kinds: Entry['kind'][]) => (data?.entries || []).filter((e) => kinds.includes(e.kind)).reduce((t, e) => t + e.credit_cents, 0)
  // The sign-up gift is credit added with no money behind it: it counts here, and is named.
  const added = sum(['deposit', 'gift'])
  const gift = sum(['gift'])
  const used = sum(['lead'])
  const back = sum(['refund', 'adjustment'])

  // Newest first, grouped by the company's own calendar day (never the UTC date:
  // an evening in Arizona is already tomorrow in UTC).
  const tz = data?.timezone || 'America/Phoenix'
  const dayKey = (d: string) => new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(d))
  const dayLabel = (d: string) => new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric' }).format(new Date(d))
  const timeOf = (d: string) => new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' }).format(new Date(d))
  const days = new Map<string, Entry[]>()
  for (const e of [...(data?.entries || [])].reverse()) {
    const k = dayKey(e.created_at)
    if (!days.has(k)) days.set(k, [])
    days.get(k)!.push(e)
  }

  return (
    <>
      <PageHeader title="Billing" subtitle="Your lead credit month by month: what came in, what each lead used, and what came back." />
      <BillingTabs />
      <div className="row-tight" style={{ marginBottom: 'var(--s-6)', alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn-ghost" aria-label="Month before" onClick={() => go(shift(data?.month || months[0], -1))}><Icon of={ChevronLeft} /></button>
        <Select aria-label="Month" value={data?.month || month || months[0]} onChange={(e) => go(e.target.value)}>
          {months.map((m) => <option key={m} value={m}>{monthName(m)}</option>)}
        </Select>
        <button type="button" className="btn btn-ghost" aria-label="Month after" disabled={(data?.month || '') >= months[0]} onClick={() => go(shift(data?.month || months[0], 1))}><Icon of={ChevronRight} /></button>
      </div>
      {error && <ErrorBlock error={error} />}
      {!data ? (!error && <LoadingBlock />) : (
        <>
          <div className="flow" aria-label="How your credit moved">
            <FlowStep label="Start of month" value={money(data.opening_credit_cents)} />
            <FlowStep label="Money added" value={signed(added)} tone="pos" meta={[data.deposited_cents ? `${money(data.deposited_cents)} paid` : '', gift ? 'plus your sign-up gift' : ''].filter(Boolean).join(', ') || 'none'} />
            <FlowStep label="Used on leads" value={signed(used)} meta={(() => { const n = (data.entries || []).filter((e) => e.kind === 'lead').length; return `${n} lead${n === 1 ? '' : 's'} from your balance` })()} />
            {back !== 0 && <FlowStep label="Refunds and corrections" value={signed(back)} tone="pos" />}
            <FlowStep label="End of month" value={money(data.closing_credit_cents)} focal />
          </div>

          <div className="wide-narrow">
            <Panel>
              <div className="panel-header"><h2 className="panel-title">Every movement</h2><span className="muted">in lead credit, at normal prices</span></div>
              {days.size === 0 ? <EmptyState title={`Nothing moved in ${monthName(data.month)}`} body="Money added, leads and refunds show up here as they happen." /> : (
                <div className="ledger">
                  {[...days.entries()].map(([day, list]) => (
                    <section key={day}>
                      <h3 className="ledger-day">{dayLabel(list[0].created_at)}</h3>
                      {list.map((e) => {
                        const row = (
                          <>
                            <span className={`ledger-icon k-${e.kind}`}><Icon of={ICON[e.kind]} size={14} /></span>
                            <span className="ledger-what">
                              <strong>{TITLE[e.kind]}{e.tier_name ? `, ${e.tier_name}` : ''}</strong>
                              <span className="muted">{e.kind === 'lead' || e.kind === 'refund' ? (e.summary || 'a lead') : [e.note, timeOf(e.created_at)].filter(Boolean).join(' · ')}</span>
                            </span>
                            <span className={e.credit_cents > 0 ? 'ledger-amt is-in' : 'ledger-amt'}>{signed(e.credit_cents)}</span>
                          </>
                        )
                        return e.lead_buyer_id
                          ? <Link key={e.id} to={`/portal/leads/${e.lead_buyer_id}`} className="ledger-row is-link">{row}</Link>
                          : <div key={e.id} className="ledger-row">{row}</div>
                      })}
                    </section>
                  ))}
                </div>
              )}
              <p className="muted" style={{ marginBottom: 0, fontSize: 13 }}>Lead credit is in normal prices. Money you add buys more credit than it costs, because prepaid leads are discounted.</p>
            </Panel>

            <Panel>
              <div className="panel-header"><h2 className="panel-title">On this month's bill</h2>
                {data.invoice.status === 'paid' ? <span className="pill is-good">Paid</span> : data.invoice.status === 'invoiced' ? <span className="pill is-warn">Due</span> : null}
              </div>
              <span className="hero-figure" style={{ fontSize: '2.2rem' }}>{money(data.invoice.invoiced_cents ?? data.invoice.total_cents)}</span>
              <p className="muted" style={{ marginTop: 'var(--s-3)' }}>
                {data.invoice.lines.length
                  ? `${data.invoice.lines.length} lead${data.invoice.lines.length === 1 ? '' : 's'} after your credit ran out, at full price. ${data.invoice.status ? '' : 'Billed on the 1st.'}`
                  : 'Every lead this month came out of your credit.'}
              </p>
              {data.invoice.lines.length > 0 && (
                <div className="row-tight" style={{ flexWrap: 'wrap' }}>
                  <Link className="btn btn-secondary" to={`/portal/invoice/${data.month}`}><Icon of={FileText} size={15} /> View invoice</Link>
                  {data.invoice.status === 'invoiced' && <Link className="btn btn-primary" to="/portal/billing">Pay</Link>}
                </div>
              )}
            </Panel>
          </div>
        </>
      )}
    </>
  )
}

function FlowStep({ label, value, meta, tone, focal }: { label: string; value: string; meta?: string; tone?: 'pos'; focal?: boolean }) {
  return (
    <div className={`flow-step${focal ? ' is-focal' : ''}`}>
      <span className="hero-kicker">{label}</span>
      <span className={`flow-value${tone === 'pos' ? ' is-in' : ''}`}>{value}</span>
      {meta && <span className="muted" style={{ fontSize: 13 }}>{meta}</span>}
    </div>
  )
}
